<template>
  <div>
    <div
      v-for="{ node, indeterminate, checked } in nodesWithState"
      :key="node.id"
      class="flex flex-col"
    >
      <div
        class="min-h-[24px] gap-8 items-center flex cursor-pointer"
        @click="toggleExpand(node.id)"
      >
        <div class="w-12 flex">
          <button
            v-if="nodeHasChildren(node)"
            type="button"
            class="bitts-tree__toggle"
            :aria-expanded="isExpanded(node.id)"
            :aria-label="`${isExpanded(node.id) ? 'Collapse' : 'Expand'} ${node.label}`"
            :disabled="expandAll || isExpansionLocked"
            @click.stop="toggleExpand(node.id)"
          >
            <FontAwesomeIcon
              :icon="['fak', 'chevron-right']"
              :style="{ height: '12px', width: '12px', color: 'currentColor' }"
              class="bitts-tree__chevron"
              :class="{
                'text-neutral-accent': !isExpanded(node.id),
                'rotate-90': isExpanded(node.id),
              }"
            />
          </button>
        </div>
        <template v-if="node.isCheckable !== false">
          <slot
            name="node-checkbox"
            :node="node"
            :checked="checked"
            :indeterminate="indeterminate"
            :on-input="(value: boolean) => onCheckboxChange(value, node)"
            :disabled="node.isDisabled || isDisabled"
          >
            <BittsCheckbox
              :checked="checked"
              :indeterminate="indeterminate"
              @input="(value) => onCheckboxChange(value, node)"
              @click.stop
              :disabled="node.isDisabled || isDisabled"
            />
          </slot>
        </template>
        <slot name="node-prefix" :node="node"></slot>
        <slot name="node-content" :node="node" :highlight-term="highlightTerm">
          <BittsTreeNodeLabel
            :label="node.label"
            :highlight-term="highlightTerm"
          />
        </slot>
        <slot name="node-suffix" :node="node"></slot>
      </div>
      <div
        v-if="isExpanded(node.id) && nodeHasChildren(node)"
        role="group"
        :aria-label="node.label"
        class="ml-28 pl-4 border-l border-neutral-border flex flex-col"
      >
        <BittsBaseTree
          :is-disabled
          :node-data="node.children ?? []"
          :get-node-indeterminate="getNodeIndeterminate"
          @update="
            (updatedNode, updatedNodeId) =>
              handleChildUpdate(updatedNode, node, updatedNodeId)
          "
          :expand-all="expandAll"
          :is-expansion-locked="isExpansionLocked"
          :highlight-term="highlightTerm"
        >
          <template #node-checkbox="slotProps">
            <slot name="node-checkbox" v-bind="slotProps" />
          </template>
          <template #node-prefix="{ node: nodeSlot }">
            <slot name="node-prefix" :node="nodeSlot" />
          </template>
          <template #node-content="{ node: nodeSlot }">
            <slot
              name="node-content"
              :node="nodeSlot"
              :highlight-term="highlightTerm"
            >
              <BittsTreeNodeLabel
                :label="nodeSlot.label"
                :highlight-term="highlightTerm"
              />
            </slot>
          </template>
          <template #node-suffix="{ node: nodeSlot }">
            <slot name="node-suffix" :node="nodeSlot"></slot>
          </template>
        </BittsBaseTree>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TMetaData">
import { computed } from 'vue';

import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';

import BittsTreeNodeLabel from './BittsTreeNodeLabel.vue';
import { type TreeNodeId, useTreeExpansion } from './useTreeExpansion';

export type TreeNodeType<TMetaData = unknown> = {
  label: string;
  checked?: boolean;
  indeterminate?: boolean;
  children?: TreeNodeType<TMetaData>[];
  id: number | string;
  isDisabled?: boolean;
  isCheckable?: boolean;
  metaData?: TMetaData;
};

type Node = TreeNodeType<TMetaData>;

const {
  expandAll = false,
  isDisabled = false,
  isExpansionLocked = false,
  initialExpandedNodes = {},
  forceExpandedIds = [],
  highlightTerm = '',
  nodeData,
  getNodeIndeterminate,
} = defineProps<{
  isDisabled?: boolean;
  nodeData: Node[];
  expandAll?: boolean;
  isExpansionLocked?: boolean;
  initialExpandedNodes?: Record<number | string, boolean>;
  forceExpandedIds?: (number | string)[];
  highlightTerm?: string;
  getNodeIndeterminate?: (node: Node) => boolean;
}>();

const emit = defineEmits<{
  update: [value: Node[], updatedNodeId: number | string];
}>();
defineSlots<{
  'node-checkbox'?(props: {
    node: Node;
    checked: boolean | undefined;
    indeterminate: boolean;
    onInput: (checked: boolean) => void;
    disabled: boolean;
  }): unknown;
  'node-prefix'?(props: { node: Node }): unknown;
  'node-content'?(props: { node: Node; highlightTerm: string }): unknown;
  'node-suffix'?(props: { node: Node }): unknown;
}>();

const expansion = useTreeExpansion({
  initialExpandedNodes,
  forceExpandedIds: () => forceExpandedIds,
  isExpansionLocked: () => isExpansionLocked,
});

const nodeHasChildren = (node: Node) => {
  return !!node.children && node.children.length > 0;
};

const isExpanded = (id: TreeNodeId) => {
  return expandAll || expansion.isExpanded(id);
};

const toggleExpand = (id: TreeNodeId) => {
  expansion.toggle(id);
};

const isIndeterminate = (node: Node): boolean => {
  if (getNodeIndeterminate) return getNodeIndeterminate(node);
  if (node.indeterminate !== undefined) return node.indeterminate;
  const children = (node.children || []).filter(
    (child) => child.isCheckable !== false,
  );
  if (!children.length) return false;
  const someChecked = children.some((child) => child.checked);
  const someIndeterminate = children.some((child) => child.indeterminate);
  return !node.checked && (someChecked || someIndeterminate);
};

const nodesWithState = computed(() =>
  nodeData.map((node) => {
    const indeterminate = isIndeterminate(node);
    return {
      node,
      indeterminate,
      checked: indeterminate ? false : node.checked,
    };
  }),
);

const onEmitUpdate = (updatedNodes: Node[], updatedNodeId: number | string) => {
  emit('update', updatedNodes, updatedNodeId);
};

const onCheckboxChange = (checked: boolean, node: Node) => {
  const updatedNode = {
    ...node,
    checked,
    children: updateChildrenCheck(node.children, checked),
  };
  handleNodeUpdate(updatedNode, node.id);
};

const updateChildrenCheck = (
  children: Node[] | undefined,
  checked: boolean,
): Node[] | undefined => {
  if (!children) return undefined;

  return children.map((child) => ({
    ...child,
    ...(child.isCheckable === false ? {} : { checked }),
    children: updateChildrenCheck(child.children, checked),
  }));
};

const handleNodeUpdate = (
  updatedNode: Node,
  updatedNodeId: number | string,
) => {
  const updatedNodeData = nodeData.map((node) =>
    node.id === updatedNode.id ? updatedNode : node,
  );
  onEmitUpdate(updatedNodeData, updatedNodeId);
};

const handleChildUpdate = (
  updatedChildren: Node[],
  parent: Node,
  updatedNodeId: number | string,
) => {
  const checkableChildren = updatedChildren.filter(
    (child) => child.isCheckable !== false,
  );
  const updatedParent = {
    ...parent,
    children: updatedChildren,
    ...(parent.isCheckable === false
      ? {}
      : {
          checked:
            checkableChildren.length > 0 &&
            checkableChildren.every((child) => child.checked),
        }),
  };
  handleNodeUpdate(updatedParent, updatedNodeId);
};
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-tree__toggle {
  @apply inline-flex items-center justify-center appearance-none border-0 bg-transparent p-0 cursor-pointer;
}

.bitts-tree__toggle:disabled {
  @apply cursor-not-allowed;
}

.bitts-tree__toggle:focus-visible {
  @apply outline-2 outline-offset-2 outline-neutral-border-focus rounded-4;
}

.bitts-tree__chevron {
  @apply ml-4 transition-transform duration-150;
}
</style>
